import React, { useRef, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { FaInfo, FaInfoCircle, FaPlus, FaTrash } from "react-icons/fa";
import {
  kachhiPaymentList,
  pakkiPaymentList,
  twoDecimal,
} from "../../config/helperFunctions";

const PaymentSection = ({ control, register, invoiceNo }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  // one-row form state
  const [form, setForm] = useState({
    paymentType: "paid",
    paymentMethod: invoiceNo ? "bank" : "cash",
    paidAmount: "",
    remarks: "",
  });

  // focus ref for Payment Type
  const typeRef = useRef(null);

  const methods = Array.isArray(
    invoiceNo ? pakkiPaymentList : kachhiPaymentList
  )
    ? (invoiceNo ? pakkiPaymentList : kachhiPaymentList).filter(Boolean)
    : [];
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const addPayment = () => {
    if (!form.paidAmount || !/^\d*\.?\d+$/.test(form.paidAmount)) {
      alert("Valid amount dalo");
      return;
    }
    append(form);
    // reset + focus Payment Type
    setForm({
      paymentType: "paid",
      paymentMethod: methods[0]?.value ?? "",
      paidAmount: "",
      remarks: "",
    });
    // next tick to ensure state applied
    setTimeout(() => {
      typeRef.current?.focus();
    }, 0);
  };

  return (
    <div
      className={`rounded-md border ${
        invoiceNo ? "border-green-400" : "border-red-400"
      } bg-white p-2 pt-1`}
    >
      {/* One-row input form */}
      <div className="p-0.5 border w-full rounded-full flex items-center gap-2">
        <FaInfoCircle size={14} className="text-gray-500" />{" "}
        <span className="text-[10px] text-red-400">
          Before adding payments please decide application type (kaccha/pakka)
          by clicking checkbox of gst in amount section block
        </span>
      </div>
      <div className="grid grid-cols-1 mt-0.5 lg:grid-cols-6 gap-2">
        <div>
          <label className="block text-[10px] font-medium text-[#4b3d91]">
            Payment Type
          </label>
          <select
            ref={typeRef}
            name="paymentType"
            value={form.paymentType}
            onChange={handleChange}
            className="mt-0.5 w-full h-6 px-1 text-[12px] border rounded-md"
          >
            <option value="paid">Paid</option>
            <option value="received">Received</option>
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-medium text-[#4b3d91]">
            Payment Method
          </label>
          <select
            name="paymentMethod"
            value={form.paymentMethod}
            onChange={handleChange}
            className="mt-0.5 w-full h-6 px-1 text-[12px] border rounded-md"
          >
            {methods?.map(({ value, label }, i) => (
              <option key={value ?? `method-${i}`} value={value ?? ""}>
                {label ?? String(value ?? "")}
              </option>
            ))}
          </select>
        </div>

     
        <>
          <div>
            <label className="block text-[10px] font-medium text-[#4b3d91]">
              Amount (₹)
            </label>
            <input
              type="text"
              name="paidAmount"
              value={form.paidAmount}
              onChange={(e) => {
                const val = e.target.value;

                // Allow empty string or valid decimal number
                if (val === "" || /^\d*\.?\d{0,4}$/.test(val)) {
                  handleChange(e); // call your original handler
                }
              }}
              placeholder="0.00"
              className="mt-0.5 w-full h-6 px-2 text-[12px] border rounded-md"
            />
          </div>

          {/* Make remarks input wider */}
          <div className="lg:col-span-2">
            <label className="block text-[10px] font-medium text-[#4b3d91]">
              Remarks
            </label>
            <input
              type="text"
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              placeholder="Note..."
              className="mt-0.5 w-full h-6 px-2 text-[12px] border rounded-md"
            />
          </div>
        </>


        <div className="flex items-end">
          <button
            type="button"
            onClick={addPayment}
            className="h-6 px-3 bg-violet-500 text-white text-xs rounded-md flex items-center gap-1"
          >
            <FaPlus size={10} /> Add
          </button>
        </div>
      </div>

      <div className="mt-2 border rounded-md overflow-hidden">
        {/* Header Table */}
        <div>
          <table className="min-w-full table-fixed text-xs text-center">
            <thead className="bg-[#9481ce] text-white">
              <tr>
                <th className="w-8 px-2 py-1">#</th>
                <th className="w-24 px-2 py-1">Type</th>
                <th className="w-28 px-2 py-1">Method</th>
                <th className="w-28 px-2 py-1">Amount</th>
                <th className="w-28 px-2 py-1">Metal</th>
                <th className="w-28 px-2 py-1">Total Wt</th>
                <th className="w-28 px-2 py-1">Fine %</th>
                <th className="w-28 px-2 py-1">Fine Paid</th>
                <th className="w-20 px-2 py-1">Action</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable Body */}
        <div className="max-h-[90px] overflow-y-auto custom-scrollbar">
          <table className="min-w-full table-fixed text-xs text-center">
            <tbody className="divide-y divide-gray-200">
              {fields?.map((f, i) => (
                <tr
                  key={f.id}
                  className="odd:bg-white even:bg-gray-50 text-[11px]"
                >
                  <td className="w-8 px-2 py-1 text-left">{i + 1}</td>
                  <td className="w-24 px-2 py-1 text-left">
                    {f?.paymentType || "-"}
                  </td>
                  <td className="w-28 px-2 py-1 text-left">
                    {f?.paymentMethod || "-"}
                  </td>
                  <td className="w-28 px-2 py-1">
                    {twoDecimal(f?.paidAmount)}
                  </td>
                  <td className="w-28 px-2 py-1">{f?.metal || "-"}</td>
                  <td className="w-28 px-2 py-1">{f?.totalWeight || "-"}</td>
                  <td className="w-28 px-2 py-1">{f?.finePercent || "-"}</td>
                  <td className="w-28 px-2 py-1 text-right">
                    {f?.finePaid || "-"}
                  </td>
                  <td className="w-20 px-2 py-1">
                    <div className="flex justify-end items-center gap-2">
                      <button
                        type="button"
                        title="Remove"
                        onClick={() => remove(i)}
                        className="text-gray-500 hover:text-red-500"
                      >
                        <FaTrash size={12} />
                      </button>
                      <button
                        title={f?.remarks}
                        type="button"
                        className="text-gray-500 hover:text-blue-500"
                      >
                        <FaInfoCircle size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentSection;
